// Generat automat din README.md
import React, { useState, useEffect } from 'react';
import { Database, Upload, Download, CheckCircle, Shield, AlertTriangle, Clock, RefreshCw, FileText, ArrowRight, Zap, Search, Filter, Eye, MoreVertical, Play, Pause } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const MigrareDate = () => {
  const [migrationStatus, setMigrationStatus] = useState('idle'); // idle, running, completed, error
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedMigration, setSelectedMigration] = useState(null);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Simulare progres migrare
  useEffect(() => {
    let interval;
    if (migrationStatus === 'running') {
      interval = setInterval(() => {
        setCurrentProgress(prev => {
          if (prev >= 100) {
            setMigrationStatus('completed');
            return 100;
          }
          return prev + Math.random() * 10;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [migrationStatus]);

  // Mock data pentru migrarea datelor
  const migrationData = {
    overview: {
      totalSystems: 12,
      completedMigrations: 8,
      activeMigrations: 2,
      pendingValidation: 1,
      dataIntegrity: 98.7,
      lastMigration: '2024-03-14 16:45:00'
    },
    systems: [
      {
        id: 'SYS-001',
        name: 'ERP Vechi (SAP)',
        type: 'ERP System',
        priority: 'Critical',
        status: 'Completed',
        dataVolume: '2.4 GB',
        recordCount: 156789,
        migrationDate: '2024-03-10 14:30:00',
        validationStatus: 'Passed',
        integrityScore: 99.2,
        issues: 12,
        resolvedIssues: 12,
        estimatedTime: '6 ore',
        actualTime: '5h 32m',
        categories: ['Facturi', 'Furnizori', 'Clienti', 'Produse']
      },
      {
        id: 'SYS-002',
        name: 'Sistem HR Legacy',
        type: 'HR Management',
        priority: 'High',
        status: 'In Progress',
        dataVolume: '890 MB',
        recordCount: 45234,
        migrationDate: '2024-03-15 09:00:00',
        validationStatus: 'Running',
        integrityScore: 95.8,
        issues: 23,
        resolvedIssues: 18,
        estimatedTime: '4 ore',
        actualTime: '2h 15m',
        categories: ['Angajati', 'Salarii', 'Concedii', 'Contracte']
      },
      {
        id: 'SYS-003',
        name: 'Excel Contabilitate',
        type: 'Spreadsheet',
        priority: 'Medium',
        status: 'Pending',
        dataVolume: '234 MB',
        recordCount: 23456,
        migrationDate: '2024-03-16 10:00:00',
        validationStatus: 'Scheduled',
        integrityScore: null,
        issues: 0,
        resolvedIssues: 0,
        estimatedTime: '2 ore',
        actualTime: null,
        categories: ['Registru Jurnal', 'Balanta', 'TVA']
      }
    ],
    validationRules: [
      {
        id: 'RULE-001',
        name: 'Validare Sume Facturi',
        type: 'Financial Integrity',
        status: 'Active',
        description: 'Verifică că suma totală factură = suma articolelor + TVA',
        successRate: 98.5,
        executionTime: '0.2s',
        lastRun: '2024-03-15 14:30:00'
      },
      {
        id: 'RULE-002',
        name: 'Verificare CNP Angajați',
        type: 'Data Validation',
        status: 'Active',
        description: 'Validează formatul și algoritmul CNP-urilor angajaților',
        successRate: 99.8,
        executionTime: '0.1s',
        lastRun: '2024-03-15 14:25:00'
      },
      {
        id: 'RULE-003',
        name: 'Reconciliere Stocuri',
        type: 'Inventory Check',
        status: 'Active',
        description: 'Verifică că stocurile din diferite sisteme se potrivesc',
        successRate: 96.2,
        executionTime: '1.5s',
        lastRun: '2024-03-15 14:20:00'
      }
    ],
    migrationHistory: [
      {
        id: 'MIG-001',
        system: 'ERP Vechi (SAP)',
        startTime: '2024-03-10 14:30:00',
        endTime: '2024-03-10 20:02:00',
        duration: '5h 32m',
        status: 'Success',
        recordsMigrated: 156789,
        errors: 12,
        warnings: 45
      },
      {
        id: 'MIG-002',
        system: 'CRM Salesforce',
        startTime: '2024-03-09 10:15:00',
        endTime: '2024-03-09 13:45:00',
        duration: '3h 30m',
        status: 'Success',
        recordsMigrated: 78456,
        errors: 3,
        warnings: 12
      }
    ]
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In Progress': return 'warning';
      case 'Pending': return 'info';
      case 'Error': return 'error';
      case 'Failed': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const getValidationColor = (status) => {
    switch (status) {
      case 'Passed': return 'success';
      case 'Running': return 'warning';
      case 'Failed': return 'error';
      case 'Scheduled': return 'info';
      default: return 'default';
    }
  };

  const filteredSystems = migrationData.systems.filter(system =>
    system.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    system.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const systemColumns = [
    { 
      header: 'Sistem Sursă', 
      key: 'name',
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{value}</div>
          <div className="text-xs text-gray-500">{row.id} • {row.type}</div>
        </div>
      )
    },
    { 
      header: 'Prioritate', 
      key: 'priority',
      render: (value) => <Badge variant={getPriorityColor(value)}>{value}</Badge>
    },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge>
    },
    { 
      header: 'Volume Date', 
      key: 'dataVolume',
      render: (value, row) => (
        <div className="text-center">
          <div className="font-semibold">{value}</div>
          <div className="text-xs text-gray-500">{row.recordCount?.toLocaleString()} înregistrări</div>
        </div>
      )
    },
    { 
      header: 'Validare', 
      key: 'validationStatus',
      render: (value) => <Badge variant={getValidationColor(value)}>{value}</Badge>
    },
    { 
      header: 'Integritate', 
      key: 'integrityScore',
      render: (value) => value ? (
        <div className="flex items-center space-x-2">
          <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${
                value > 95 ? 'bg-green-500' : value > 90 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${value}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium">{value}%</span>
        </div>
      ) : (
        <span className="text-xs text-gray-400">Pending</span>
      )
    },
    { 
      header: 'Progres', 
      key: 'progress',
      render: (_, row) => {
        const progress = row.status === 'Completed' ? 100 : 
                        row.status === 'In Progress' ? currentProgress :
                        row.status === 'Pending' ? 0 : 50;
        return (
          <div className="w-20">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  row.status === 'Completed' ? 'bg-green-500' : 
                  row.status === 'In Progress' ? 'bg-blue-500' : 'bg-gray-400'
                }`}
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className="text-xs text-center mt-1">{Math.round(progress)}%</div>
          </div>
        );
      }
    },
    {
      header: 'Acțiuni',
      key: 'actions',
      render: (_, row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setSelectedMigration(row);
              setShowValidationModal(true);
            }}
            className="text-primary-600 hover:text-primary-800 text-sm"
            title="Vezi Detalii"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button 
            className="text-gray-600 hover:text-gray-800 text-sm" 
            title="Opțiuni"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  const validationColumns = [
    { header: 'Regula', key: 'name' },
    { header: 'Tip', key: 'type' },
    { header: 'Status', key: 'status', render: (value) => <Badge variant="success">{value}</Badge> },
    { header: 'Success Rate', key: 'successRate', render: (value) => `${value}%` },
    { header: 'Timp Execuție', key: 'executionTime' },
    { header: 'Ultima Rulare', key: 'lastRun' }
  ];

  const historyColumns = [
    { header: 'ID Migrare', key: 'id' },
    { header: 'Sistem', key: 'system' },
    { header: 'Durată', key: 'duration' },
    { header: 'Status', key: 'status', render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge> },
    { header: 'Înregistrări', key: 'recordsMigrated', render: (value) => value.toLocaleString() },
    { header: 'Erori', key: 'errors' }
  ];

  const startMigration = (systemId) => {
    setMigrationStatus('running');
    setCurrentProgress(0);
    // Simulare începere migrare
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Migrare Date Contabilitate</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Transfer automat de date din sistemele existente cu validare și reconciliere completă
          </p>
        </div>
        <div className="flex space-x-3">
          <button className="btn-secondary">
            <Download className="w-4 h-4 mr-2" />
            Export Log
          </button>
          <button 
            className="btn-primary"
            onClick={() => setShowMigrationModal(true)}
          >
            <Upload className="w-4 h-4 mr-2" />
            Migrare Nouă
          </button>
        </div>
      </div>

      {/* Overview Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
        <Card title="Total Sisteme" subtitle="De migrat" icon={Database}>
          <div className="text-2xl font-bold text-primary-600">{migrationData.overview.totalSystems}</div>
          <div className="text-sm text-blue-600">Identificate în audit</div>
        </Card>
        <Card title="Migrări Complete" subtitle="Finalizate cu succes" icon={CheckCircle}>
          <div className="text-2xl font-bold text-green-600">{migrationData.overview.completedMigrations}</div>
          <div className="text-sm text-green-600">100% validare</div>
        </Card>
        <Card title="În Progres" subtitle="Rulând acum" icon={RefreshCw}>
          <div className="text-2xl font-bold text-yellow-600">{migrationData.overview.activeMigrations}</div>
          <div className="text-sm text-yellow-600">
            {migrationStatus === 'running' && <RefreshCw className="w-3 h-3 inline animate-spin mr-1" />}
            Real-time
          </div>
        </Card>
        <Card title="Pending Validare" subtitle="Necesită verificare" icon={Clock}>
          <div className="text-2xl font-bold text-orange-600">{migrationData.overview.pendingValidation}</div>
          <div className="text-sm text-orange-600">Aprobare manuală</div>
        </Card>
        <Card title="Integritate Date" subtitle="Scor general" icon={Shield}>
          <div className="text-2xl font-bold text-green-600">{migrationData.overview.dataIntegrity}%</div>
          <div className="text-sm text-green-600">Peste target 95%</div>
        </Card>
        <Card title="Ultima Migrare" subtitle="Activitate recentă" icon={ArrowRight}>
          <div className="text-sm font-bold text-primary-600">
            {new Date(migrationData.overview.lastMigration).toLocaleDateString('ro-RO')}
          </div>
          <div className="text-xs text-gray-500">ERP Vechi → Sistem Nou</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Migration Systems Table */}
        <div className="lg:col-span-3">
          <Card title="Sisteme pentru Migrare" subtitle="Status și progres migrare date">
            <div className="mb-4 flex items-center space-x-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Caută sistem sau tip..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <button className="btn-secondary">
                <Filter className="w-4 h-4 mr-2" />
                Filtre
              </button>
              <button 
                className={`btn-secondary ${migrationStatus === 'running' ? 'bg-red-100' : 'bg-green-100'}`}
                onClick={() => setMigrationStatus(migrationStatus === 'running' ? 'idle' : 'running')}
              >
                {migrationStatus === 'running' ? (
                  <>
                    <Pause className="w-4 h-4 mr-2" />
                    Stop All
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Start Batch
                  </>
                )}
              </button>
            </div>
            <Table columns={systemColumns} data={filteredSystems} />
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card title="Progres General" icon={RefreshCw}>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm">Sisteme Complete</span>
                <span className="text-sm font-medium">8/12</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-green-400 to-blue-500 h-3 rounded-full transition-all duration-300" 
                  style={{ width: '67%' }}
                ></div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div className="text-center p-2 bg-green-50 dark:bg-green-900/20 rounded">
                  <div className="font-bold text-green-600">8</div>
                  <div className="text-green-700 dark:text-green-300">Finalizate</div>
                </div>
                <div className="text-center p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                  <div className="font-bold text-yellow-600">4</div>
                  <div className="text-yellow-700 dark:text-yellow-300">Rămase</div>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Acțiuni Rapide">
            <div className="space-y-3">
              <button 
                className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                onClick={() => setShowMigrationModal(true)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Migrare Nouă</span>
                  <Upload className="w-4 h-4 text-gray-400" />
                </div>
              </button>
              
              <button className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Verificare Integritate</span>
                  <CheckCircle className="w-4 h-4 text-gray-400" />
                </div>
              </button>
              
              <button className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Backup Complet</span>
                  <Database className="w-4 h-4 text-gray-400" />
                </div>
              </button>
            </div>
          </Card>
        </div>
      </div>

      {/* Validation Rules */}
      <Card title="Reguli de Validare Active" subtitle="Verificări automate pentru integritatea datelor">
        <Table columns={validationColumns} data={migrationData.validationRules} />
      </Card>

      {/* Migration History */}
      <Card title="Istoric Migrări" subtitle="Migrările completate recent">
        <Table columns={historyColumns} data={migrationData.migrationHistory} />
      </Card>

      {/* Migration Setup Modal */}
      <Modal
        isOpen={showMigrationModal}
        onClose={() => setShowMigrationModal(false)}
        title="Configurare Migrare Nouă"
        size="large"
      >
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Selectare Sistem Sursă</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tip Sistem
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white">
                  <option>ERP System</option>
                  <option>CRM System</option>
                  <option>HR Management</option>
                  <option>Accounting Software</option>
                  <option>Excel/CSV Files</option>
                  <option>Database (SQL/NoSQL)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Prioritate Migrare
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white">
                  <option>Critical</option>
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Configurare Conexiune</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Host/Server
                </label>
                <input
                  type="text"
                  placeholder="192.168.1.100 sau server.company.com"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Port
                </label>
                <input
                  type="number"
                  placeholder="1433, 5432, 3306..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Opțiuni Migrare</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="backup" defaultChecked className="rounded" />
                <label htmlFor="backup" className="text-sm">Creare backup înainte de migrare</label>
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="validate" defaultChecked className="rounded" />
                <label htmlFor="validate" className="text-sm">Validare automată integritate date</label>
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="incremental" className="rounded" />
                <label htmlFor="incremental" className="text-sm">Migrare incrementală (doar date noi)</label>
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="schedule" className="rounded" />
                <label htmlFor="schedule" className="text-sm">Programare pentru execuție în afara orelor de lucru</label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button 
              className="btn-secondary"
              onClick={() => setShowMigrationModal(false)}
            >
              Anulează
            </button>
            <button className="btn-secondary">
              Test Conexiune
            </button>
            <button className="btn-primary">
              <Database className="w-4 h-4 mr-2" />
              Start Migrare
            </button>
          </div>
        </div>
      </Modal>

      {/* Validation Detail Modal */}
      <Modal
        isOpen={showValidationModal}
        onClose={() => {
          setShowValidationModal(false);
          setSelectedMigration(null);
        }}
        title={`Detalii Migrare: ${selectedMigration?.name}`}
        size="large"
      >
        {selectedMigration && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Informații Sistem</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>Sistem:</span>
                    <span className="font-medium">{selectedMigration.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tip:</span>
                    <span>{selectedMigration.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Prioritate:</span>
                    <Badge variant={getPriorityColor(selectedMigration.priority)}>
                      {selectedMigration.priority}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Volume Date:</span>
                    <span className="font-medium">{selectedMigration.dataVolume}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Înregistrări:</span>
                    <span className="font-medium">{selectedMigration.recordCount?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Status Migrare</h3>
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                      Status General
                    </div>
                    <Badge variant={getStatusColor(selectedMigration.status)}>
                      {selectedMigration.status}
                    </Badge>
                  </div>
                  {selectedMigration.integrityScore && (
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div className="text-sm font-medium text-green-800 dark:text-green-200 mb-1">
                        Integritate Date
                      </div>
                      <div className="text-2xl font-bold text-green-600">
                        {selectedMigration.integrityScore}%
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Categorii Date</h3>
              <div className="flex flex-wrap gap-2">
                {selectedMigration.categories.map((category, index) => (
                  <Badge key={index} variant="info" className="text-xs">
                    {category}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Timp Execuție</h3>
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span>Estimat:</span>
                    <span className="font-medium">{selectedMigration.estimatedTime}</span>
                  </div>
                  {selectedMigration.actualTime && (
                    <div className="flex justify-between">
                      <span>Actual:</span>
                      <span className="font-medium text-green-600">{selectedMigration.actualTime}</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Issues</h3>
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span>Total Issues:</span>
                    <span className="font-medium text-yellow-600">{selectedMigration.issues}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Rezolvate:</span>
                    <span className="font-medium text-green-600">{selectedMigration.resolvedIssues}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Rămase:</span>
                    <span className="font-medium text-red-600">{selectedMigration.issues - selectedMigration.resolvedIssues}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button className="btn-secondary">View Logs</button>
              <button className="btn-secondary">Re-run Validation</button>
              <button className="btn-primary">Export Report</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MigrareDate;